import styled from 'styled-components';

import { Text } from '@/nova/components/Text/Text';
import { ANIM, COLORS, MOBILE, SPACE } from '@/nova/theme/tokens';

/* The market's collections as a list and its detail, after the "Pick a collection" sketch: the casinos down the left,
   the chosen one's collections on the right under its title and a search, in groups by where they sit on the casino's
   site, three cards across. Each column scrolls on its own, filling the room the list editor gives the step. On a phone
   the casinos are a row that scrolls sideways over one column of cards. Colours and timings are tokens, spacing is the
   SPACE scale. */

export const Root = styled.section`
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: 250px minmax(0, 1fr);

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
    gap: ${SPACE[16]};
  }
`;

/* ----------------------------------------------------------- the casinos */

export const Side = styled.div`
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
  padding: ${SPACE[8]} ${SPACE[20]} ${SPACE[8]} 0;
  border-right: 1px solid ${COLORS.hairline};

  ${MOBILE} {
    overflow: visible;
    padding: 0;
    border-right: 0;
  }
`;

export const SideTitle = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${COLORS.textMuted};

  ${MOBILE} {
    display: none;
  }
`;

export const Sites = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};

  ${MOBILE} {
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: ${SPACE[4]};
  }
`;

/* One casino: its mark, its name and how many collections it has (that match the search). `$on`: the chosen one.
   `$none`: no collection matches the search there; it stays reachable, only quieter. */
export const Site = styled.button<{ $on: boolean; $none: boolean }>`
  flex: none;
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  padding: ${SPACE[8]} ${SPACE[12]} ${SPACE[8]} ${SPACE[8]};
  border: 0;
  border-radius: 12px;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  color: ${({ $none }) => ($none ? COLORS.textFaint : COLORS.text)};
  background: ${({ $on }) => ($on ? COLORS.ctaTint : 'transparent')};
  transition: background-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    background: ${({ $on }) => ($on ? COLORS.ctaTint : COLORS.surfaceFaint)};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.ctaSolid};
    outline-offset: 2px;
  }

  ${MOBILE} {
    padding-right: ${SPACE[16]};
    border: 1px solid ${({ $on }) => ($on ? COLORS.ctaBorder : COLORS.hairline)};
    border-radius: 999px;
  }
`;

/* The casino's mark: its initials, or a star for the market's own collections; filled in the call-to-action colour
   when chosen. */
export const SiteMark = styled.span<{ $on: boolean }>`
  flex: none;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  color: ${({ $on }) => ($on ? COLORS.ctaSolidText : COLORS.text)};
  background: ${({ $on }) => ($on ? COLORS.ctaSolid : COLORS.surfaceInset)};
`;

export const SiteName = styled.span<{ $on: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 15px;
  font-weight: ${({ $on }) => ($on ? 700 : 500)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SiteCount = styled.span<{ $on: boolean }>`
  flex: none;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $on }) => ($on ? COLORS.ctaSolid : COLORS.textMuted)};
`;

/* ----------------------------------------------------------- the chosen casino's collections */

export const Main = styled.div`
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/* The title and its line on the left, the search on the right; one under the other on a phone. */
export const Head = styled.div`
  flex: none;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACE[16]} ${SPACE[24]};
  padding: ${SPACE[8]} ${SPACE[4]} ${SPACE[20]} ${SPACE[24]};
  border-bottom: 1px solid ${COLORS.hairline};
  /* The same scrollbar gutter as the list under it, so the search ends where the cards do. */
  overflow: hidden;
  scrollbar-gutter: stable;

  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
    padding: 0 0 ${SPACE[16]};
  }
`;

export const HeadText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
`;

/* A plain heading, so the step can hand it the keyboard (see the editor's jumpTo); no ring of its own, as the step
   headings have none. */
export const Title = styled.h3`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: ${COLORS.heading};

  &:focus {
    outline: none;
  }
`;

export const Hint = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 14px;
  color: ${COLORS.textMuted};
`;

export const SearchWrap = styled.label`
  flex: 0 1 340px;
  position: relative;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: ${SPACE[12]};
    color: ${COLORS.textMuted};
    pointer-events: none;
  }

  ${MOBILE} {
    flex-basis: auto;
  }
`;

export const Search = styled.input`
  width: 100%;
  height: 44px;
  box-sizing: border-box;
  padding: 0 ${SPACE[12]} 0 ${SPACE[40]};
  border-radius: 12px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 14px;
  outline: none;
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:focus {
    border-color: ${COLORS.accent};
  }

  &::placeholder {
    color: ${COLORS.textFaint};
  }
`;

/* The groups, scrolling on their own under the head; on a phone they flow with the sheet. */
export const Scroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[24]};
  padding: ${SPACE[24]} ${SPACE[4]} ${SPACE[24]} ${SPACE[24]};
  scrollbar-gutter: stable;

  ${MOBILE} {
    overflow-y: visible;
    padding: ${SPACE[16]} 0 0;
  }
`;

export const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
`;

/* Where a group sits: the page, then its category in the call-to-action colour, a rule across, and the count. */
export const GroupHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
`;

export const GroupTitle = styled.h4`
  flex: none;
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${COLORS.textMuted};
`;

export const GroupCategory = styled.span`
  color: ${COLORS.ctaSolid};
`;

export const GroupRule = styled.span`
  flex: 1 1 auto;
  height: 1px;
  background: ${COLORS.hairline};
`;

export const GroupCount = styled.span`
  flex: none;
  font-size: 13px;
  color: ${COLORS.textMuted};
`;

/* Three cards across in the editor's sheet, one on a phone. */
export const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: ${SPACE[16]};

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/* One collection, the label of its radio, so the whole card picks it: its first games, its name and the radio's mark,
   then how many games it has and how many the catalogue carries. `$on`: the one selected. `$off`: none of its games
   are in the catalogue (or the catalogue is still loading); its radio is disabled. */
export const Card = styled.label<{ $on: boolean; $off: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
  padding: ${SPACE[16]};
  border-radius: 16px;
  cursor: ${({ $off }) => ($off ? 'default' : 'pointer')};
  opacity: ${({ $off }) => ($off ? 0.5 : 1)};
  background: ${({ $on }) => ($on ? COLORS.ctaTint : COLORS.surfaceFaint)};
  border: 2px solid ${({ $on }) => ($on ? COLORS.ctaBorder : COLORS.hairline)};
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${({ $on, $off }) => ($on ? COLORS.ctaBorder : $off ? COLORS.hairline : COLORS.hoverBorderLit)};
  }

  /* The ring while the radio has the keyboard's focus, the radio itself being out of sight. */
  &:has(input:focus-visible) {
    outline: 2px solid ${COLORS.ctaSolid};
    outline-offset: 2px;
  }
`;

/* Out of sight but still the control, for assistive tech and the keyboard: the card shows the choice. */
export const Radio = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

export const Tiles = styled.span`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: ${SPACE[8]};
`;

/* A game's art, or its name on a plain tile when the catalogue has none. */
export const Tile = styled.span`
  position: relative;
  aspect-ratio: 10 / 9;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  padding: ${SPACE[8]};
  background: ${COLORS.surfaceInset};
  font-size: 11px;
  font-weight: 700;
  line-height: 1.15;
  text-transform: uppercase;
  color: ${COLORS.text};

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const TileName = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const NameRow = styled.span`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
`;

export const Name = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${COLORS.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* The radio's mark: a ring, with a dot in the call-to-action colour when selected. */
export const Mark = styled.span<{ $on: boolean }>`
  flex: none;
  width: 22px;
  height: 22px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 2px solid ${({ $on }) => ($on ? COLORS.ctaSolid : COLORS.textFaint)};
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ $on }) => ($on ? COLORS.ctaSolid : 'transparent')};
  }
`;

export const Meta = styled.span`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACE[8]} ${SPACE[12]};
  font-size: 13px;
  color: ${COLORS.textMuted};
`;

export const GamesChip = styled.span`
  padding: ${SPACE[2]} ${SPACE[8]};
  border-radius: 6px;
  background: ${COLORS.surfaceInset};
  color: ${COLORS.text};
  font-weight: 700;
`;

export const Carried = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};

  svg {
    color: ${COLORS.ctaSolid};
  }
`;

/* Loading, nothing published, a failed read, no match: one line in the middle of the room, an action under it. */
export const State = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[12]};
  padding: ${SPACE[32]} ${SPACE[16]};
  text-align: center;
  color: ${COLORS.textMuted};
  font-size: 14px;
`;
